import type { InjectionKey, Ref } from 'vue'

// Which shell frames the route-agnostic chat core. Set once, literally, by
// each host adapter — never derived from route/store state downstream.
export type HostType = 'web-page' | 'web-sidebar' | 'webview'

// Confirmation dialog descriptor for "start a new chat". The host decides how
// to surface it (webview: native alert; web: RevDialog) and runs onAccept /
// onDecline accordingly.
export type NewChatDialog = {
  title: string
  description: string
  acceptButtonText: string
  declineButtonText: string
  onAccept: () => void
  onDecline?: (() => void) | undefined
}

type CommonHost = {
  // Inbound message pushed by the host (webview native 'chat'); '' when none.
  receivedEvent: Readonly<Ref<string>>
  clearReceivedEvent: () => void
  // Dismiss the assistant (webview: close webview; web-sidebar: hide the
  // panel; web-page: navigate away).
  close: () => void
  // Confirm, then start a new chat (webview: native alert; web: RevDialog).
  newChat: (dialog: NewChatDialog) => void
}

// Extensions provided by either web host (sidebar or page) — the two share
// this shape, only their close()/newChat() implementations differ.
export type WebShoppingAssistantHost = CommonHost & {
  hostType: 'web-page' | 'web-sidebar'
  // Report whether the conversation holds a real user message, so the host can
  // gate destructive confirmations (skips the prompt on an empty chat).
  reportConversation: (hasUserMessages: boolean) => void
  // Reflect a freshly created chat id. The web host has no chat URL to sync
  // (unlike the webview), but uses this to know when it's safe to push
  // browsing context for the currently viewed page.
  syncChatId: (id: string) => void
  // Notified when a product card link is clicked, as its product-page
  // navigation starts. Only the web-sidebar implements it: in overlay
  // (small-viewport) mode the panel covers the screen, so it hides itself to
  // reveal the navigation; the pinned desktop panel stays open. The web-page
  // host omits it — the route change already unmounts the chat.
  onProductCardClick?: () => void
}

// Extensions provided only by the webview host.
export type WebviewShoppingAssistantHost = CommonHost & {
  hostType: 'webview'
  // Signal the host the chat UI is ready (sends native 'ready').
  ready: () => void
  // Notify the host the cart changed (native bridge).
  addToCart: () => void
  // Reflect a freshly created chat id in the URL.
  syncChatId: (id: string) => void
  // Navigate to a fresh chat (route push).
  goToNewChat: () => void
}

/**
 * The seam between the route-agnostic shopping-assistant core and whatever
 * frames it. Each host (native webview, web floating container) provides its
 * own implementation; the core only ever talks to this type.
 */
export type ShoppingAssistantHost =
  | WebShoppingAssistantHost
  | WebviewShoppingAssistantHost

export const SHOPPING_ASSISTANT_HOST_KEY = Symbol(
  'shoppingAssistantHost',
) as InjectionKey<ShoppingAssistantHost>
